import { Indicators } from '@round/whitelabel-ui';
import { useAudioStatsIndicators } from './hooks/useAudioStatsIndicators';
import { Tooltip } from '@round/whitelabel-ui';
import { SkeletonizedValue } from '@round/ui-kit';
import { formatNumberToKNotation, numberWithCommas } from 'helpers';
import styles from './Indicators.module.css';
import { useCurrentSpotifyPopularity } from './hooks/useLatestSpotifyPopularity';

type Props = {
    songId: number | null | undefined;
};

const SongIndicators = ({ songId }: Props) => {
    const { data: audioStatsIndicators, isFetching, isError } = useAudioStatsIndicators(songId);

    const {
        data: spotifyPopularity,
        isLoading: isSpotifyPopularityLoading,
        shouldExpectNoData: shouldExpectNoSpotifyPopularity,
    } = useCurrentSpotifyPopularity(songId);

    return (
        <Indicators.Container>
            {!shouldExpectNoSpotifyPopularity && (
                <Indicators.Item>
                    <Indicators.Label tooltip="The Spotify Popularity Index is a 0-100 score based on the total number of plays for a track and how recent those plays are">
                        Spotify Popularity
                    </Indicators.Label>
                    <Indicators.Value>
                        <SkeletonizedValue isInitialized={!isSpotifyPopularityLoading} width="4rem">
                            {typeof spotifyPopularity === 'number' ? (
                                <>
                                    <span>{spotifyPopularity}</span>
                                    <span className={styles.denominator}>/100</span>
                                </>
                            ) : (
                                '-'
                            )}
                        </SkeletonizedValue>
                    </Indicators.Value>
                </Indicators.Item>
            )}
            {audioStatsIndicators.map((indicator) => (
                <Indicators.Item key={indicator.label}>
                    <Indicators.Label>{indicator.label}</Indicators.Label>
                    <Tooltip.Root>
                        {(anchor, setAnchorRef) => (
                            <>
                                <Indicators.Value ref={setAnchorRef}>
                                    <SkeletonizedValue isInitialized={!isFetching} width="4rem">
                                        {typeof indicator.total === 'number'
                                            ? formatNumberToKNotation(indicator.total)
                                            : '-'}
                                    </SkeletonizedValue>
                                </Indicators.Value>

                                <Tooltip
                                    anchorElement={anchor}
                                    disabled={isFetching || isError || typeof indicator.total !== 'number'}
                                >
                                    <Tooltip.Title>{indicator.label}</Tooltip.Title>
                                    <Tooltip.Body>
                                        <dl className={styles.tooltipStats}>
                                            <div className={styles.tooltipStatsItem}>
                                                <dt>Total:</dt>
                                                <dd>
                                                    {typeof indicator.total === 'number'
                                                        ? numberWithCommas(indicator.total)
                                                        : '-'}
                                                </dd>
                                            </div>
                                            <hr />
                                            <div className={styles.tooltipStatsItem}>
                                                <dt>TikTok:</dt>
                                                <dd>
                                                    {typeof indicator.tiktok === 'number'
                                                        ? numberWithCommas(indicator.tiktok)
                                                        : '-'}
                                                </dd>
                                            </div>
                                            <div className={styles.tooltipStatsItem}>
                                                <dt>Instagram:</dt>
                                                <dd>
                                                    {typeof indicator.instagram === 'number'
                                                        ? numberWithCommas(indicator.instagram)
                                                        : '-'}
                                                </dd>
                                            </div>
                                        </dl>
                                    </Tooltip.Body>
                                </Tooltip>
                            </>
                        )}
                    </Tooltip.Root>
                </Indicators.Item>
            ))}
        </Indicators.Container>
    );
};

export default SongIndicators;
